import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { Handshake, Download, ArrowRight } from "lucide-react";

const PARTNERS = [
  { key: "artar", name: "ARTAR Real Estate Development", sector: { en: "Property developer", ar: "تطوير عقاري" } },
  { key: "kaizen", name: "Kaizen Asset Management", sector: { en: "Asset management", ar: "إدارة الأصول" } },
  { key: "hunter-foods", name: "Hunter Foods", sector: { en: "F&B manufacturing", ar: "تصنيع الأغذية" } },
  { key: "intero", name: "Intero Services", sector: { en: "Integrated services", ar: "خدمات متكاملة" } },
  { key: "les-toises", name: "Les Toises Wellbeing", sector: { en: "Wellbeing centre", ar: "مركز عافية" } },
  { key: "epson", name: "Epson", sector: { en: "Corporate offices", ar: "مكاتب الشركات" } },
  { key: "unique-properties", name: "Unique Properties", sector: { en: "Real estate", ar: "عقارات" } },
  { key: "royal-touch", name: "The Royal Touch Vacation Homes", sector: { en: "Vacation homes", ar: "بيوت العطلات" } },
  { key: "wec", name: "WEC International Mechanical Engineering", sector: { en: "MEP engineering", ar: "هندسة ميكانيكية" } },
];

const COPY = {
  en: {
    eyebrow: "Our Partners",
    h1: "Trusted by the brands that keep the UAE running.",
    intro: "Developers, manufacturers, wellbeing operators and corporate offices across the Emirates rely on Orion to keep their spaces clean, safe and running.",
    download: "Download",
    some: "Some of our partners",
    ctaTitle: "Become our next partner.",
    cta: "Get a Free Quote",
  },
  ar: {
    eyebrow: "شركاؤنا",
    h1: "موثوقون من العلامات التي تُبقي الإمارات تعمل.",
    intro: "مطورون ومصنّعون ومراكز عافية ومكاتب شركات في جميع أنحاء الإمارات يعتمدون على أوريون للحفاظ على مرافقهم نظيفة وآمنة وتعمل بكفاءة.",
    download: "تحميل",
    some: "بعض شركائنا",
    ctaTitle: "كن شريكنا القادم.",
    cta: "احصل على عرض سعر مجاني",
  },
};

const PartnerCard = ({ p, lang, label, i }) => {
  const src = `/partners/orion_x_${p.key}.png`;
  return (
    <figure
      data-testid={`partner-card-${p.key}`}
      className="group bg-slate-900 border border-white/10 rounded-3xl overflow-hidden hover:border-amber-300/40 transition-colors duration-300 fade-up"
      style={{ animationDelay: `${i * 60}ms` }}
    >
      <div className="overflow-hidden">
        <img src={src} alt={`Orion FM × ${p.name}`} loading="lazy" className="w-full aspect-square object-cover group-hover:scale-[1.03] transition-transform duration-500" />
      </div>
      <figcaption className="p-5 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div dir="ltr" className="font-heading text-white font-medium truncate text-start">{p.name}</div>
          <div className="text-xs text-slate-400 mt-1">{p.sector[lang] || p.sector.en}</div>
        </div>
        <a
          href={src}
          download={`orion_x_${p.key}.png`}
          data-testid={`partner-download-${p.key}`}
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-amber-300 hover:text-slate-950 border border-white/15 px-4 py-2 text-xs font-semibold text-white transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          {label}
        </a>
      </figcaption>
    </figure>
  );
};

export default function Partners() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;

  return (
    <div data-testid="partners-page" className="bg-slate-950 text-white">
      <SEO
        title={`Our Partners & Clients | ${COMPANY.brand}`}
        description="Orion FM is trusted by ARTAR, Kaizen, Hunter Foods, Intero, Les Toises, Epson, Unique Properties, The Royal Touch and WEC for facilities management across the UAE."
      />

      <section className="container-orion pt-20 lg:pt-28 pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-amber-300 border border-amber-300/30 rounded-full px-3 py-1.5">
              <Handshake className="w-3.5 h-3.5" />
              {c.eyebrow}
            </div>
            <h1 data-testid="partners-title" className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05]">
              {c.h1}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl">{c.intro}</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <div data-testid="partners-some-label" className="flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-semibold text-amber-300">
              <span className="w-10 h-px bg-amber-300" />
              {c.some}
            </div>
          </div>
        </div>
      </section>

      <section className="container-orion pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PARTNERS.map((p, i) => (
            <PartnerCard key={p.key} p={p} lang={lang} label={c.download} i={i} />
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 py-20">
        <div className="container-orion flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-medium">{c.ctaTitle}</h2>
          <Link
            to="/contact"
            data-testid="partners-cta-quote"
            className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
          >
            {c.cta}
            <ArrowRight className="w-4 h-4 rtl-flip" />
          </Link>
        </div>
      </section>
    </div>
  );
}
